import { useState, useEffect, useRef } from "react";
import { PaisModal, getPaises } from "./PaisModal";
import { ChevronsUpDown, Copy, Check } from "lucide-react";
import { AduanaModal, getAduanas } from "./AduanaModal";
import { useDispatch, useSelector } from "react-redux";
import {
  ModosTransporteSoivreModal,
  getModosTransporteSoivre,
  getModosTransporteFronteraPredefinidos,
} from "./ModosTransporteSoivreModal";
import {
  MedioTransporteFronteraModal,
  getMediosTransporteFrontera,
} from "./MedioTransporteFronteraModal";
import {
  ExportacionesTipoModal,
  getExportacionesTipos,
} from "./ExportacionesTipoModal";
import {
  TipoUbicacionModal,
  getTipoUbicacionOptions,
} from "./TipoUbicacionModal";
import { DivisasModal, getDivisas } from "./DivisasModal";
import { DivisasISOModal, getDivisasISO } from "./DivisasISOModal";
import {
  CondicionesEntregaModal,
  getCondicionesEntrega,
} from "./CondicionesEntregaModal";
import {
  ClaveDeclaranteModal,
  getClavesDeclarante,
} from "./ClaveDeclaranteModal";
import {
  Declaracion1Modal,
  getDeclaraciones1,
} from "./Declaracion1Modal";
import {
  Declaracion2Modal,
  getDeclaraciones2,
} from "./Declaracion2Modal";

// Reusable Input with Copy Button Component
const InputWithCopy = ({ id, value, placeholder, onChange, className, type = "text", required = false, maxLength, min, max }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (value === null || value === undefined || value === '') return;
    
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Check if value exists (including 0 as a valid value)
  const hasValue = value !== null && value !== undefined && value !== '';

  // Handle value properly - don't convert 0 to empty string
  const inputValue = value === null || value === undefined ? "" : value;

  return (
    <div className="relative">
      <input
        id={id}
        type={type}
        value={inputValue}
        placeholder={placeholder}
        onChange={onChange}
        className={className}
        required={required}
        maxLength={maxLength}
        min={min}
        max={max}
      />
      {hasValue && (
        <button
          type="button"
          onClick={handleCopy}
          className="absolute right-0 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors"
          title={copied ? "Copied!" : "Copy"}
        >
          {copied ? (
            <Check className="w-4 h-4 text-green-500" />
          ) : (
            <Copy className="w-4 h-4 text-gray-500" />
          )}
        </button>
      )}
    </div>
  );
};

// Reusable Binary Toggle Component (0/1 selector)
const BinaryToggle = ({ id, value, onChange, label0, label1 }) => {
  const isSelected0 = value === 0;
  const isSelected1 = value === 1;
  const isUnselected = value === null || value === undefined;

  const handleClick = (newValue) => {
    if (onChange) {
      // Create a synthetic event-like object for consistency
      const syntheticEvent = {
        target: { value: newValue }
      };
      onChange(syntheticEvent);
    }
  };

  return (
    <div id={id} className="flex gap-1 mt-1">
      <button
        id={id ? `${id}_0` : undefined}
        type="button"
        onClick={() => handleClick(0)}
        className={`flex items-center justify-center gap-1.5 py-1 rounded-full transition-colors ${
          isSelected0
            ? 'bg-secondary text-default font-semibold px-3'
            : isUnselected
            ? 'bg-gray-200 text-gray-600 px-4'
            : 'bg-gray-200 text-gray-400 px-4'
        }`}
      >
        {isSelected0 && <Check className="w-3 h-3" />}
        <span className="text-xs">{label0 || '0'}</span>
      </button>
      <button
        id={id ? `${id}_1` : undefined}
        type="button"
        onClick={() => handleClick(1)}
        className={`flex items-center justify-center gap-2 py-1 rounded-full transition-colors ${
          isSelected1
            ? 'bg-secondary text-default font-semibold px-3'
            : isUnselected
            ? 'bg-gray-200 text-gray-600 px-4'
            : 'bg-gray-200 text-gray-400 px-4'
        }`}
      >
        {isSelected1 && <Check className="w-3 h-3" />}
        <span className="text-xs">{label1 || '1'}</span>
      </button>
    </div>
  );
};

export const DeclarationInformation = ({
  handleChange,
  declarationIndex,
  isSpanish,
  declaration,
  editableData,
  setEditableData,
  setIsDirty,
}) => {
  const userData = useSelector((state) => state.user.userData);
  const [isPaisDestinoModalOpen, setPaisDestinoModalOpen] = useState(false);
  const [isPaisExportacionModalOpen, setPaisExportacionModalOpen] =
    useState(false);
  const [
    isPaisTransporteFronteraModalOpen,
    setPaisTransporteFronteraModalOpen,
  ] = useState(false);
  const [isAduanaEntradaModalOpen, setAduanaEntradaModalOpen] = useState(false);
  const [isAduanaSalidaModalOpen, setAduanaSalidaModalOpen] = useState(false);
  const [isAduanaDespachoModalOpen, setAduanaDespachoModalOpen] =
    useState(false);
  const [isModoFronteraModalOpen, setModoFronteraModalOpen] = useState(false);
  const [isMedioFronteraModalOpen, setMedioFronteraModalOpen] = useState(false);
  const [isExportacionesTipoModalOpen, setExportacionesTipoModalOpen] =
    useState(false);
  const [isPaisUbicacionModalOpen, setPaisUbicacionModalOpen] = useState(false);
  const [isTipoUbicacionModalOpen, setTipoUbicacionModalOpen] = useState(false);
  const [isDivisaModalOpen, setDivisaModalOpen] = useState(false);
  const [isDivisaIsoModalOpen, setDivisaIsoModalOpen] = useState(false);
  const [isCondicionesEntregaModalOpen, setCondicionesEntregaModalOpen] =
    useState(false);
  const [isClaveDeclaranteModalOpen, setClaveDeclaranteModalOpen] = useState(false);
  const [isDeclaracion1ModalOpen, setDeclaracion1ModalOpen] = useState(false);
  const [isDeclaracion2ModalOpen, setDeclaracion2ModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("All");

  // find the selected country objects for display
  const paises = getPaises();
  const selectedPaisDestino = paises.find(
    (p) =>
      p.codigo === declaration.pais_destino ||
      p.nombre === declaration.pais_destino
  );
  const selectedPaisExportacion = paises.find(
    (p) =>
      p.codigo === declaration.pais_exportacion ||
      p.nombre === declaration.pais_exportacion
  );
  const selectedPaisTransporteFrontera = paises.find(
    (p) =>
      p.codigo === declaration.pais_transporte_frontera ||
      p.nombre === declaration.pais_transporte_frontera
  );
  const selectedPais = paises.find(
    (p) => p.codigo === declaration.pais || p.nombre === declaration.pais
  );
  const selectedPaisUbicacion = paises.find(
    (p) =>
      p.codigo === declaration.pais_ubicacion ||
      p.nombre === declaration.pais_ubicacion
  );

  // find the selected aduana objects for display
  const aduanasEntrada = getAduanas('aduanas-espanolas');
  const aduanasSalida = getAduanas(['aduanas-comunitarias-espanolas', 'aduanas-comunitarias-no-espanolas']);
  const aduanasDespacho = getAduanas('aduanas-espanolas');
  
  const selectedAduanaEntrada = aduanasEntrada.find(
    (a) =>
      a.codigo === declaration.aduana_entrada ||
      a.nombre === declaration.aduana_entrada
  );
  const selectedAduanaSalida = aduanasSalida.find(
    (a) =>
      a.codigo === declaration.aduana_salida ||
      a.nombre === declaration.aduana_salida
  );
  const selectedAduanaDespacho = aduanasDespacho.find(
    (a) =>
      a.codigo === declaration.aduana_despacho ||
      a.nombre === declaration.aduana_despacho
  );

  // find the selected modo objects for display
  const modosTransporte = getModosTransporteSoivre();
  // Use predefined list for modo_transporte_frontera
  const modosTransporteFronteraPredefinidos = getModosTransporteFronteraPredefinidos();
  const selectedModoFrontera = modosTransporteFronteraPredefinidos.find(
    (m) =>
      m.codigo === declaration.modo_transporte_frontera ||
      m.nombre === declaration.modo_transporte_frontera
  );
  const selectedModoInterior = modosTransporte.find(
    (m) =>
      m.codigo === declaration.modo_transporte_interior ||
      m.nombre === declaration.modo_transporte_interior
  );

  // find the selected medio objects for display (from taric_tables_data.json)
  const mediosTransporteFrontera = getMediosTransporteFrontera();
  // Separate medio_transporte_frontera (transport means) from modo_transporte_frontera (transport mode)
  const selectedMedioTransporteFrontera = mediosTransporteFrontera.find(
    (m) =>
      m.codigo === declaration.medio_transporte_frontera ||
      m.nombre === declaration.medio_transporte_frontera
  );

  // find the selected export type for display
  const exportacionesTipos = getExportacionesTipos();
  const selectedExportacionesTipo = exportacionesTipos.find(
    (t) => t.codigo === declaration.tipo || t.nombre === declaration.tipo
  );

  // find the selected tipo ubicacion for display
  const tipoUbicacionOptions = getTipoUbicacionOptions();
  const selectedTipoUbicacion = tipoUbicacionOptions.find(
    (t) =>
      t.codigo === declaration.tipo_ubicacion ||
      t.nombre === declaration.tipo_ubicacion
  );

  const divisas = getDivisasISO();
  const selectedDivisa = divisas.find(
    (d) => d.iso === declaration.divisa || d.codigo === declaration.divisa
  );
  const selectedDivisaIso = divisas.find(
    (d) =>
      d.iso === declaration.divisa_iso || d.codigo === declaration.divisa_iso
  );

  // find the selected clave declarante for display
  const clavesDeclarante = getClavesDeclarante();
  const selectedClaveDeclarante = clavesDeclarante.find(
    (c) =>
      c.codigo === declaration.clave_declarante ||
      c.nombre === declaration.clave_declarante
  );

  // find the selected declaracion 1 for display
  const declaraciones1 = getDeclaraciones1();
  const selectedDeclaracion1 = declaraciones1.find(
    (d) =>
      d.codigo === declaration.declaracion_1 ||
      d.nombre === declaration.declaracion_1
  );

  // find the selected declaracion 2 for display
  const declaraciones2 = getDeclaraciones2();
  const selectedDeclaracion2 = declaraciones2.find(
    (d) =>
      d.codigo === declaration.declaracion_2 ||
      d.nombre === declaration.declaracion_2
  );

  // Get current timestamp in YYYY-MM-DDTHH:MM:SS format
  const getCurrentTimestamp = () => {
    const now = new Date();
    return now.toISOString().slice(0, 19); // YYYY-MM-DDTHH:MM:SS
  };

  // Track if we've initialized this declaration's fecha_creacion
  const initializedRef = useRef(new Set());

  // Initialize fecha_creacion with current date and time (timestamp) on mount
  useEffect(() => {
    const key = `${declarationIndex}`;
    if (!initializedRef.current.has(key)) {
      const currentTimestamp = getCurrentTimestamp();
      handleChange(declarationIndex, "fecha_creacion", currentTimestamp);
      initializedRef.current.add(key);
    }
  }, [declarationIndex]);

  // handlers for selecting a country
  const handlePaisDestinoSelect = (pais) => {
    handleChange(declarationIndex, "pais_destino", pais.codigo);
  };
  const handlePaisExportacionSelect = (pais) => {
    handleChange(declarationIndex, "pais_exportacion", pais.codigo);
  };
  const handlePaisTransporteFronteraSelect = (pais) => {
    handleChange(declarationIndex, "pais_transporte_frontera", pais.codigo);
  };
  const handlePaisSelect = (pais) => {
    handleChange(declarationIndex, "pais", pais.codigo);
  };

  // handlers for selecting an aduana from the modals
  const handleAduanaEntradaSelect = (aduana) => {
    handleChange(declarationIndex, "aduana_entrada", aduana.codigo);
  };
  const handleAduanaSalidaSelect = (aduana) => {
    handleChange(declarationIndex, "aduana_salida", aduana.codigo);
  };
  const handleAduanaDespachoSelect = (aduana) => {
    handleChange(declarationIndex, "aduana_despacho", aduana.codigo);
  };

  // handlers for selecting a modo from the modals
  const handleModoFronteraSelect = (modo) => {
    handleChange(declarationIndex, "modo_transporte_frontera", modo.codigo);
  };
  const handleModoInteriorSelect = (modo) => {
    handleChange(declarationIndex, "modo_transporte_interior", modo.codigo);
  };

  // handlers for selecting a medio from the modals
  const handleMedioFronteraSelect = (medio) => {
    handleChange(declarationIndex, "medio_transporte_frontera", medio.codigo);
  };

  // handler for selecting export type
  const handleExportacionesTipoSelect = (tipo) => {
    handleChange(declarationIndex, "tipo", tipo.codigo);
  };

  const handlePaisUbicacionSelect = (pais) => {
    handleChange(declarationIndex, "pais_ubicacion", pais.codigo);
  };

  // handler for selecting clave declarante
  const handleClaveDeclaranteSelect = (clave) => {
    handleChange(declarationIndex, "clave_declarante", clave.codigo);
  };

  // handler for selecting declaracion 1
  const handleDeclaracion1Select = (declaracion) => {
    handleChange(declarationIndex, "declaracion_1", declaracion.codigo);
  };

  // handler for selecting declaracion 2
  const handleDeclaracion2Select = (declaracion) => {
    handleChange(declarationIndex, "declaracion_2", declaracion.codigo);
  };

  const handleTipoUbicacionSelect = (tipo) => {
    handleChange(declarationIndex, "tipo_ubicacion", tipo.codigo);
  };

  const handleDivisaSelect = (divisa) => {
    handleChange(declarationIndex, "divisa", divisa.codigo);
  };

  const handleDivisaIsoSelect = (divisa) => {
    handleChange(declarationIndex, "divisa_iso", divisa.iso);
  };

  // Tab definitions
  const tabs = [
    { id: "All", label: !isSpanish ? "All" : "Todo" },
    { id: "Expediente", label: !isSpanish ? "Expediente" : "Expediente" },
    { id: "Declaración", label: !isSpanish ? "Declaración" : "Declaración" },
    { id: "Datos Transporte", label: !isSpanish ? "Datos Transporte" : "Datos Transporte" },
    { id: "Información Adicional", label: !isSpanish ? "Información Adicional" : "Información Adicional" },
    { id: "Importes", label: !isSpanish ? "Importes" : "Importes" },
  ];

  return (
    <>
      {/* Tab Navigation */}
      <div className="pt-2 mt-4 overflow-x-auto mb-4">
        <div className="flex gap-2 items-center pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`h-10 rounded-full text-sm cursor-pointer hover:opacity-75 transition-opacity py-1 whitespace-nowrap flex-shrink-0 ${
                activeTab === tab.id
                  ? 'bg-secondary font-semibold px-4'
                  : 'px-2'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="grid grid-cols-2 gap-8 rounded-md">
        {/* EXPEDIENTE TAB */}
        {(activeTab === "All" || activeTab === "Expediente") && (
          <>
            {/* Declaration Type & Date */}
            <div className="split-modal flex flex-col gap-4 rounded-md flex-1">
          <h2 className="text-sm font-bold">
            {!isSpanish
              ? "Declaration Type and Date"
              : "Tipo y Fecha de Declaración"}
          </h2>
          <div className={`form-group flex-1`}>
            <label
              htmlFor="tipo"
              className={`${!declaration.tipo && "text-red-600"}`}
            >
              {!isSpanish ? "Type" : "Tipo"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`tipo_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedExportacionesTipo ? "" : "opacity-75"
                }`}
                onClick={() => setExportacionesTipoModalOpen(true)}
              >
                {selectedExportacionesTipo ? (
                  <span className="truncate">
                    {selectedExportacionesTipo.codigo}{" "}
                    {selectedExportacionesTipo.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedExportacionesTipo.nombre})
                      </span>
                    )}
                  </span>
                ) : declaration.tipo ? (
                  <span className="truncate" title={declaration.tipo}>
                    {declaration.tipo}
                  </span>
                ) : (
                  <span className="truncate">
                    {!isSpanish
                      ? "Select Export Type"
                      : "Seleccionar Tipo de Exportación"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              <ExportacionesTipoModal
                tipos={exportacionesTipos}
                isOpen={isExportacionesTipoModalOpen}
                onClose={() => setExportacionesTipoModalOpen(false)}
                onSelect={handleExportacionesTipoSelect}
                selectedTipo={selectedExportacionesTipo}
                title={
                  !isSpanish
                    ? "Select Export Type"
                    : "Seleccionar Tipo de Exportación"
                }
              />
            </div>
          </div>
          <div className="form-group flex-1">
            <label
              htmlFor="fecha_registro"
            >
              {!isSpanish ? "Registration Date" : "Fecha Registro"}
            </label>
            <InputWithCopy
              id={`fecha_registro_${declarationIndex + 1}`}
              value={declaration.fecha_registro}
              placeholder={`${
                !declaration.fecha_registro && !isSpanish
                  ? "Enter Registration Date"
                  : "Entrada Fecha Registro"
              }`}
              onChange={(e) =>
                handleChange(declarationIndex, "fecha_registro", e.target.value)
              }
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
          <div className="form-group flex-1">
            <label
              htmlFor="fecha_creacion"
            >
              {!isSpanish ? "Creation Date" : "Fecha Creación"}
            </label>
            <InputWithCopy
              id={`fecha_creacion_${declarationIndex + 1}`}
              type="text"
              value={declaration.fecha_creacion || ""}
              placeholder="YYYY-MM-DDTHH:MM:SS"
              onChange={(e) =>
                handleChange(declarationIndex, "fecha_creacion", e.target.value)
              }
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
          {/* Fecha Oper. - Missing field from PDF */}
          <div className="form-group flex-1">
            <label
              htmlFor="fecha_operacion"
            >
              {!isSpanish ? "Operation Date" : "Fecha Operación"}
            </label>
            <InputWithCopy
              id={`fecha_operacion_${declarationIndex + 1}`}
              type="text"
              value={declaration.fecha_operacion || ""}
              placeholder="YYYY-MM-DDTHH:MM:SS"
              onChange={(e) =>
                handleChange(declarationIndex, "fecha_operacion", e.target.value)
              }
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
        </div>

        {/* Declaration Identification */}
        <div className="split-modal flex flex-col gap-4 rounded-md flex-1">
          <h2 className="text-sm font-bold">
            {!isSpanish
              ? "Declaration Identification"
              : "Identificación de la Declaración"}
          </h2>
          {/* MRN */}
          <div className="form-group flex-1">
            <label
              htmlFor="mrn"
            >
              {!isSpanish ? "MRN" : "MRN (Número de Referencia del Movimiento)"}
            </label>
            <InputWithCopy
              id={`mrn_${declarationIndex + 1}`}
              value={declaration.mrn || ""}
              placeholder={`${
                !declaration.mrn && !isSpanish
                  ? "Enter MRN (e.g. 25ES000441122632A5)"
                  : "Entrada MRN (Ej. 25ES000441122632A5)"
              }`}
              onChange={(e) =>
                handleChange(declarationIndex, "mrn", e.target.value)
              }
              className="w-full border rounded px-2 py-1 pr-8"
              maxLength={35}
            />
          </div>
          <div className="form-group flex-1">
            <label
              htmlFor="declaracion_1"
            >
              {!isSpanish ? "Declaration 1" : "Declaración 1"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`declaracion_1_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedDeclaracion1 ? "" : "opacity-75"
                }`}
                onClick={() => setDeclaracion1ModalOpen(true)}
              >
                {selectedDeclaracion1 ? (
                  <span
                    className="truncate"
                    style={{ maxWidth: "200px", display: "inline-block" }}
                    title={`${selectedDeclaracion1.codigo} - ${selectedDeclaracion1.nombre}`}
                  >
                    {selectedDeclaracion1.codigo}{" "}
                    {selectedDeclaracion1.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedDeclaracion1.nombre.substring(0, 50)}...)
                      </span>
                    )}
                  </span>
                ) : declaration.declaracion_1 ? (
                  <span
                    className="truncate"
                    title={declaration.declaracion_1}
                  >
                    {declaration.declaracion_1}
                  </span>
                ) : (
                  <span
                    className="truncate"
                    title={
                      !isSpanish
                        ? "Select Declaration 1"
                        : "Seleccionar Declaración 1"
                    }
                  >
                    {!isSpanish
                      ? "Select Declaration 1"
                      : "Seleccionar Declaración 1"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              <Declaracion1Modal
                declaraciones={declaraciones1}
                isOpen={isDeclaracion1ModalOpen}
                onClose={() => setDeclaracion1ModalOpen(false)}
                onSelect={handleDeclaracion1Select}
                selectedDeclaracion={selectedDeclaracion1}
                title={
                  !isSpanish
                    ? "Select Declaration 1"
                    : "Seleccionar Declaración 1"
                }
                isSpanish={isSpanish}
              />
            </div>
          </div>
          <div className="form-group flex-1">
            <label
              htmlFor="declaracion_2"
            >
              {!isSpanish ? "Declaration 2" : "Declaración 2"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`declaracion_2_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedDeclaracion2 ? "" : "opacity-75"
                }`}
                onClick={() => setDeclaracion2ModalOpen(true)}
              >
                {selectedDeclaracion2 ? (
                  <span
                    className="truncate"
                    style={{ maxWidth: "200px", display: "inline-block" }}
                    title={`${selectedDeclaracion2.codigo} - ${selectedDeclaracion2.nombre}`}
                  >
                    {selectedDeclaracion2.codigo}{" "}
                    {selectedDeclaracion2.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedDeclaracion2.nombre.substring(0, 50)}...)
                      </span>
                    )}
                  </span>
                ) : declaration.declaracion_2 ? (
                  <span
                    className="truncate"
                    title={declaration.declaracion_2}
                  >
                    {declaration.declaracion_2}
                  </span>
                ) : (
                  <span
                    className="truncate"
                    title={
                      !isSpanish
                        ? "Select Declaration 2"
                        : "Seleccionar Declaración 2"
                    }
                  >
                    {!isSpanish
                      ? "Select Declaration 2"
                      : "Seleccionar Declaración 2"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              <Declaracion2Modal
                declaraciones={declaraciones2}
                isOpen={isDeclaracion2ModalOpen}
                onClose={() => setDeclaracion2ModalOpen(false)}
                onSelect={handleDeclaracion2Select}
                selectedDeclaracion={selectedDeclaracion2}
                title={
                  !isSpanish
                    ? "Select Declaration 2"
                    : "Seleccionar Declaración 2"
                }
                isSpanish={isSpanish}
              />
            </div>
          </div>
          <div className="form-group flex-1">
            <label
              htmlFor="declaracion_3"
            >
              {!isSpanish ? "Declaration 3" : "Declaración 3"}
            </label>
            <InputWithCopy
              id={`declaracion_3_${declarationIndex + 1}`}
              value={declaration.declaracion_3}
              placeholder={`${
                !declaration.declaracion_3 && !isSpanish
                  ? "Enter Declaration 3"
                  : "Entrada Declaración 3"
              }`}
              onChange={(e) =>
                handleChange(declarationIndex, "declaracion_3", e.target.value)
              }
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
        </div>

        {/* Operators/Parties References */}
        <div className="split-modal flex flex-col gap-4 rounded-md flex-1">
          <h2 className="text-sm font-bold">
            {!isSpanish ? "Operators Information" : "Información de Operadores"}
          </h2>
          <div className="form-group">
            <label
              htmlFor="cliente_id"
              className={`${!declaration.cliente_id && "text-red-600"}`}
            >
              {!isSpanish ? "Client ID" : "Cliente ID"}
            </label>
            <InputWithCopy
              id={`cliente_id_${declarationIndex + 1}`}
              value={declaration.cliente_id}
              placeholder={`${
                !declaration.cliente_id && !isSpanish
                  ? "Enter Client ID"
                  : "Entrada Cliente ID"
              }`}
              onChange={(e) =>
                handleChange(declarationIndex, "cliente_id", e.target.value)
              }
              className={`w-full border rounded px-2 py-1 pr-8 ${
                !declaration.cliente_id && "required-input"
              }`}
              required
            />
          </div>
          <div className="form-group">
            <label
              htmlFor="remitente_id"
              className={`${!declaration.remitente_id && "text-red-600"}`}
            >
              {!isSpanish ? "Sender ID" : "Remitente ID"}
            </label>
            <InputWithCopy
              id={`remitente_id_${declarationIndex + 1}`}
              value={declaration.remitente_id}
              placeholder={`${
                !declaration.remitente_id && !isSpanish
                  ? "Enter Sender ID"
                  : "Entrada Remitente ID"
              }`}
              onChange={(e) =>
                handleChange(declarationIndex, "remitente_id", e.target.value)
              }
              className={`w-full border rounded px-2 py-1 pr-8 ${
                !declaration.remitente_id && "required-input"
              }`}
              required
            />
          </div>
          {/* Clave Declarante - Missing field from PDF */}
          <div className="form-group">
            <label
              htmlFor="clave_declarante"
            >
              {!isSpanish ? "Declarant Key" : "Clave Declarante"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`clave_declarante_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedClaveDeclarante ? "" : "opacity-75"
                }`}
                onClick={() => setClaveDeclaranteModalOpen(true)}
              >
                {selectedClaveDeclarante ? (
                  <span
                    className="truncate"
                    style={{ maxWidth: "200px", display: "inline-block" }}
                    title={`${selectedClaveDeclarante.codigo} - ${selectedClaveDeclarante.nombre}`}
                  >
                    {selectedClaveDeclarante.codigo}{" "}
                    {selectedClaveDeclarante.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedClaveDeclarante.nombre})
                      </span>
                    )}
                  </span>
                ) : declaration.clave_declarante ? (
                  <span
                    className="truncate"
                    title={declaration.clave_declarante}
                  >
                    {declaration.clave_declarante}
                  </span>
                ) : (
                  <span
                    className="truncate"
                    title={
                      !isSpanish
                        ? "Select Declarant Key"
                        : "Seleccionar Clave Declarante"
                    }
                  >
                    {!isSpanish
                      ? "Select Declarant Key"
                      : "Seleccionar Clave Declarante"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              <ClaveDeclaranteModal
                claves={clavesDeclarante}
                isOpen={isClaveDeclaranteModalOpen}
                onClose={() => setClaveDeclaranteModalOpen(false)}
                onSelect={handleClaveDeclaranteSelect}
                selectedClave={selectedClaveDeclarante}
                title={
                  !isSpanish
                    ? "Select Declarant Key"
                    : "Seleccionar Clave Declarante"
                }
              />
            </div>
          </div>
        </div>

        {/* Packages */}
        <div className="split-modal flex flex-col gap-4 rounded-md flex-1">
          <h2 className="text-sm font-bold">
            {!isSpanish ? "Packaging Information" : "Información de Embalaje"}
          </h2>
          <div className="form-group">
            <label
              htmlFor="tiene_contenedores"
            >
              {!isSpanish ? "Has Containers" : "Tiene Contenedores"}
            </label>
            <InputWithCopy
              id={`tiene_contenedores_${declarationIndex + 1}`}
              value={declaration.tiene_contenedores}
              placeholder={`${
                !declaration.tiene_contenedores && !isSpanish
                  ? "Enter Has Containers"
                  : "Entrada Tiene Contenedores"
              }`}
              onChange={(e) =>
                handleChange(
                  declarationIndex,
                  "tiene_contenedores",
                  e.target.value
                )
              }
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
          <div className="form-group">
            <label
              htmlFor="total_bultos"
            >
              {!isSpanish ? "Total Packages" : "Total Bultos"}
            </label>
            <InputWithCopy
              id={`total_bultos_${declarationIndex + 1}`}
              value={declaration.total_bultos}
              placeholder={`${
                !declaration.total_bultos && !isSpanish
                  ? "Enter Total Packages"
                  : "Entrada Total Bultos"
              }`}
              onChange={(e) =>
                handleChange(declarationIndex, "total_bultos", e.target.value)
              }
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
        </div>
          </>
        )}

        {/* DECLARACIÓN TAB */}
        {(activeTab === "All" || activeTab === "Declaración") && (
          <>
        {/* Customs Information */}
        <div className="split-modal flex flex-col gap-4 rounded-md flex-1">
          <h2 className="text-sm font-bold">
            {!isSpanish ? "Customs Information" : "Información Aduanera"}
          </h2>
          {/* aduana_entrada custom selector */}
          <div className="form-group">
            <label
              htmlFor="aduana_entrada"
            >
              {!isSpanish ? "Entry Customs" : "Aduana Entrada"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`aduana_entrada_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedAduanaEntrada ? "" : "opacity-75"
                }`}
                onClick={() => setAduanaEntradaModalOpen(true)}
              >
                {selectedAduanaEntrada ? (
                  <span className="truncate">
                    {selectedAduanaEntrada.codigo}{" "}
                    {selectedAduanaEntrada.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedAduanaEntrada.nombre})
                      </span>
                    )}
                  </span>
                ) : declaration.aduana_entrada ? (
                  <span className="truncate" title={declaration.aduana_entrada}>
                    {declaration.aduana_entrada}
                  </span>
                ) : (
                  <span className="truncate">
                    {!isSpanish
                      ? "Select Entry Customs"
                      : "Seleccionar Aduana Entrada"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              <AduanaModal
                isOpen={isAduanaEntradaModalOpen}
                onClose={() => setAduanaEntradaModalOpen(false)}
                onSelect={handleAduanaEntradaSelect}
                selectedAduana={selectedAduanaEntrada}
                title={
                  !isSpanish
                    ? "Select Entry Customs"
                    : "Seleccionar Aduana Entrada"
                }
                dataSource="aduanas-espanolas"
              />
            </div>
          </div>
          {/* aduana_salida custom selector */}
          <div className="form-group">
            <label
              htmlFor="aduana_salida"
            >
              {!isSpanish ? "Exit Customs" : "Aduana Salida"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`aduana_salida_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedAduanaSalida ? "" : "opacity-75"
                }`}
                onClick={() => setAduanaSalidaModalOpen(true)}
              >
                {selectedAduanaSalida ? (
                  <span className="truncate">
                    {selectedAduanaSalida.codigo}{" "}
                    {selectedAduanaSalida.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedAduanaSalida.nombre})
                      </span>
                    )}
                  </span>
                ) : declaration.aduana_salida ? (
                  <span className="truncate" title={declaration.aduana_salida}>
                    {declaration.aduana_salida}
                  </span>
                ) : (
                  <span className="truncate">
                    {!isSpanish
                      ? "Select Exit Customs"
                      : "Seleccionar Aduana Salida"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              <AduanaModal
                isOpen={isAduanaSalidaModalOpen}
                onClose={() => setAduanaSalidaModalOpen(false)}
                onSelect={handleAduanaSalidaSelect}
                selectedAduana={selectedAduanaSalida}
                title={
                  !isSpanish
                    ? "Select Exit Customs"
                    : "Seleccionar Aduana Salida"
                }
                dataSource={['aduanas-comunitarias-espanolas', 'aduanas-comunitarias-no-espanolas']}
              />
            </div>
          </div>
          {/* aduana_despacho custom selector */}
          <div className="form-group">
            <label
              htmlFor="aduana_despacho"
            >
              {!isSpanish ? "Clearance Customs" : "Aduana Despacho"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`aduana_despacho_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedAduanaDespacho ? "" : "opacity-75"
                }`}
                onClick={() => setAduanaDespachoModalOpen(true)}
              >
                {selectedAduanaDespacho ? (
                  <span className="truncate">
                    {selectedAduanaDespacho.codigo}{" "}
                    {selectedAduanaDespacho.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedAduanaDespacho.nombre})
                      </span>
                    )}
                  </span>
                ) : declaration.aduana_despacho ? (
                  <span
                    className="truncate"
                    title={declaration.aduana_despacho}
                  >
                    {declaration.aduana_despacho}
                  </span>
                ) : (
                  <span className="truncate">
                    {!isSpanish
                      ? "Select Clearance Customs"
                      : "Seleccionar Aduana Despacho"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              <AduanaModal
                isOpen={isAduanaDespachoModalOpen}
                onClose={() => setAduanaDespachoModalOpen(false)}
                onSelect={handleAduanaDespachoSelect}
                selectedAduana={selectedAduanaDespacho}
                title={
                  !isSpanish
                    ? "Select Clearance Customs"
                    : "Seleccionar Aduana Despacho"
                }
                dataSource="aduanas-espanolas"
              />
            </div>
          </div>
        </div>
          </>
        )}

        {/* DATOS TRANSPORTE TAB */}
        {(activeTab === "All" || activeTab === "Datos Transporte") && (
          <>
        {/* Transport */}
        <div className="split-modal flex flex-col gap-4 rounded-md flex-1">
          <h2 className="text-sm font-bold">
            {!isSpanish ? "Transport Details" : "Detalles de Transporte"}
          </h2>
          <div className="form-group">
            <label
              htmlFor="modo_transporte_frontera"
            >
              {!isSpanish
                ? "Border Transport Mode"
                : "Modo Transporte Frontera"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`modo_transporte_frontera_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedModoFrontera ? "" : "opacity-75"
                }`}
                onClick={() => setModoFronteraModalOpen(true)}
              >
                {selectedModoFrontera ? (
                  <span
                    className="truncate"
                    style={{ maxWidth: "200px", display: "inline-block" }}
                    title={`${selectedModoFrontera.codigo} - ${selectedModoFrontera.nombre}`}
                  >
                    {selectedModoFrontera.codigo}{" "}
                    {selectedModoFrontera.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedModoFrontera.nombre})
                      </span>
                    )}
                  </span>
                ) : declaration.modo_transporte_frontera ? (
                  <span
                    className="truncate"
                    title={declaration.modo_transporte_frontera}
                  >
                    {declaration.modo_transporte_frontera}
                  </span>
                ) : (
                  <span
                    className="truncate"
                    title={
                      !isSpanish
                        ? "Select Border Transport Mode"
                        : "Seleccionar Modo Transporte Frontera"
                    }
                  >
                    {!isSpanish
                      ? "Select Border Transport Mode"
                      : "Seleccionar Modo Transporte Frontera"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              <ModosTransporteSoivreModal
                modos={modosTransporteFronteraPredefinidos}
                isOpen={isModoFronteraModalOpen}
                onClose={() => setModoFronteraModalOpen(false)}
                onSelect={handleModoFronteraSelect}
                selectedModo={selectedModoFrontera}
                title={
                  !isSpanish
                    ? "Select Border Transport Mode"
                    : "Seleccionar Modo Transporte Frontera"
                }
              />
            </div>
          </div>
          <div className="form-group">
            <label
              htmlFor="modo_transporte_interior"
            >
              {!isSpanish
                ? "Internal Transport Mode"
                : "Modo Transporte Interior"}
            </label>
            <InputWithCopy
              id={`modo_transporte_interior_${declarationIndex + 1}`}
              value={declaration.modo_transporte_interior}
              placeholder={`${
                !declaration.modo_transporte_interior && !isSpanish
                  ? "Enter Internal Transport Mode"
                  : "Entrada Modo Transporte Interior"
              }`}
              onChange={(e) =>
                handleChange(
                  declarationIndex,
                  "modo_transporte_interior",
                  e.target.value
                )
              }
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
          <div className="form-group flex-1">
            <label
              htmlFor="identidad_transporte_frontera"
            >
              {!isSpanish
                ? "Border Transport Identity"
                : "Identidad Transporte Frontera"}
            </label>
            <InputWithCopy
              id={`identidad_transporte_frontera_${declarationIndex + 1}`}
              value={declaration.identidad_transporte_frontera}
              placeholder={`${
                !declaration.identidad_transporte_frontera && !isSpanish
                  ? "Enter Border Transport Identity"
                  : "Entrada Identidad Transporte Frontera"
              }`}
              onChange={(e) =>
                handleChange(
                  declarationIndex,
                  "identidad_transporte_frontera",
                  e.target.value
                )
              }
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
          {/* Medio Transporte Frontera - Missing field from PDF (separate from modo_transporte_frontera) */}
          <div className="form-group">
            <label
              htmlFor="medio_transporte_frontera"
            >
              {!isSpanish
                ? "Transport Means (Border)"
                : "Medio Transporte Frontera"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`medio_transporte_frontera_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedMedioTransporteFrontera ? "" : "opacity-75"
                }`}
                onClick={() => setMedioFronteraModalOpen(true)}
              >
                {selectedMedioTransporteFrontera ? (
                  <span
                    className="truncate"
                    style={{ maxWidth: "200px", display: "inline-block" }}
                    title={`${selectedMedioTransporteFrontera.codigo} - ${selectedMedioTransporteFrontera.nombre}`}
                  >
                    {selectedMedioTransporteFrontera.codigo}{" "}
                    {selectedMedioTransporteFrontera.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedMedioTransporteFrontera.nombre})
                      </span>
                    )}
                  </span>
                ) : declaration.medio_transporte_frontera ? (
                  <span
                    className="truncate"
                    title={declaration.medio_transporte_frontera}
                  >
                    {declaration.medio_transporte_frontera}
                  </span>
                ) : (
                  <span className="truncate">
                    {!isSpanish
                      ? "Select Transport Means"
                      : "Seleccionar Medio Transporte Frontera"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              <MedioTransporteFronteraModal
                medios={mediosTransporteFrontera}
                isOpen={isMedioFronteraModalOpen}
                onClose={() => setMedioFronteraModalOpen(false)}
                onSelect={handleMedioFronteraSelect}
                selectedMedio={selectedMedioTransporteFrontera}
                title={
                  !isSpanish
                    ? "Select Transport Means"
                    : "Seleccionar Medio Transporte Frontera"
                }
              />
            </div>
          </div>
        </div>
          </>
        )}

        {/* INFORMACIÓN ADICIONAL TAB */}
        {(activeTab === "All" || activeTab === "Información Adicional") && (
          <>
        {/* EDI Status */}
        <div className="split-modal flex flex-col gap-4 rounded-md flex-1">
          <h2 className="text-sm font-bold">
            {!isSpanish ? "EDI Status" : "Estado EDI"}
          </h2>
          <div className="form-group">
            <label htmlFor="autorizacion_por_operacion">
              {!isSpanish
                ? "Authorization per Operation (0/1)"
                : "Autorización por Operación (0/1)"}
            </label>
            <BinaryToggle
              id={`autorizacion_por_operacion_${declarationIndex + 1}`}
              value={declaration.autorizacion_por_operacion}
              onChange={(e) =>
                handleChange(
                  declarationIndex,
                  "autorizacion_por_operacion",
                  e.target.value
                )
              }
            />
          </div>
         
          {/* Datos de Seguridad - Missing field from PDF */}
          <div className="form-group">
            <label htmlFor="flag_datos_seguridad">
              {!isSpanish
                ? "Security Data (0/1)"
                : "Datos de Seguridad (0/1)"}
            </label>
            <BinaryToggle
              id={`flag_datos_seguridad_${declarationIndex + 1}`}
              value={declaration.flag_datos_seguridad}
              onChange={(e) =>
                handleChange(
                  declarationIndex,
                  "flag_datos_seguridad",
                  e.target.value
                )
              }
            />
          </div>
          <div className="form-group">
            <label htmlFor="es_simplificado">
              {!isSpanish ? "Simplified (0/1)" : "Simplificado (0/1)"}
            </label>
            <BinaryToggle
              id={`es_simplificado_${declarationIndex + 1}`}
              value={declaration.es_simplificado}
              onChange={(e) =>
                handleChange(
                  declarationIndex,
                  "es_simplificado",
                  e.target.value
                )
              }
            />
          </div>
          <div className="form-group">
            <label htmlFor="es_recapitulativo">
              {!isSpanish ? "Recapitulative (0/1)" : "Recapitulativo (0/1)"}
            </label>
            <BinaryToggle
              id={`es_recapitulativo_${declarationIndex + 1}`}
              value={declaration.es_recapitulativo}
              onChange={(e) =>
                handleChange(
                  declarationIndex,
                  "es_recapitulativo",
                  e.target.value
                )
              }
            />
          </div>
           {/* New: Status/Authorization fields */}
           <div className="form-group">
            <label htmlFor="estado_pdc">
              {!isSpanish ? "PDC Status" : "Estado PDC"}
            </label>
            <InputWithCopy
              id={`estado_pdc_${declarationIndex + 1}`}
              value={declaration.estado_pdc ?? ""}
              type="number"
              placeholder={!isSpanish ? "e.g. 0" : "Ej. 0"}
              onChange={(e) =>
                handleChange(declarationIndex, "estado_pdc", e.target.value === "" ? 0 : Number(e.target.value))
              }
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
          <div className="form-group">
            <label
              htmlFor="estado_edi"
            >
              {!isSpanish ? "EDI Status" : "Estado EDI"}
            </label>
            <InputWithCopy
              id={`estado_edi_${declarationIndex + 1}`}
              value={declaration.estado_edi}
              type="number"
              placeholder={`${
                !declaration.estado_edi && !isSpanish
                  ? "Enter EDI Statu"
                  : "Entrada Estado EDI"
              }`}
              onChange={(e) =>
                handleChange(declarationIndex, "estado_edi", e.target.value)
              }
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
          <div className="form-group">
            <label
              htmlFor="estado_edi_canarias"
            >
              {!isSpanish ? "Canary Islands EDI Status" : "Estado EDI Canarias"}
            </label>
            <InputWithCopy
              id={`estado_edi_canarias_${declarationIndex + 1}`}
              value={declaration.estado_edi_canarias}
              type="number"
              placeholder={`${
                !declaration.estado_edi_canarias && !isSpanish
                  ? "Enter Canary Islands EDI Status"
                  : "Entrada Estado EDI Canarias"
              }`}
              onChange={(e) =>
                handleChange(
                  declarationIndex,
                  "estado_edi_canarias",
                  e.target.value
                )
              }
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
        </div>
        {/* Other Information */}
        <div className="split-modal flex flex-col gap-4 rounded-md flex-1">
          <h2 className="text-sm font-bold">
            {!isSpanish ? "Additional Information" : "Información Adicional"}
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="form-group ">
              <label htmlFor={`completo_${declarationIndex + 1}`}>
                {!isSpanish ? "Complete (0/1)" : "Completo (0/1)"}
              </label>
              <BinaryToggle
                id={`completo_${declarationIndex + 1}`}
                value={declaration.completo}
                onChange={(e) =>
                  handleChange(
                    declarationIndex,
                    "completo",
                    e.target.value
                  )
                }
              />
            </div>
            <div className="form-group">
              <label htmlFor={`es_plantilla_${declarationIndex + 1}`}>
                {!isSpanish ? "Is Template (0/1)" : "¿Es Plantilla (0/1)?"}
              </label>
              <BinaryToggle
                id={`es_plantilla_${declarationIndex + 1}`}
                value={declaration.es_plantilla}
                onChange={(e) =>
                  handleChange(
                    declarationIndex,
                    "es_plantilla",
                    e.target.value
                  )
                }
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="form-group">
              <label htmlFor="e_mail">
                {!isSpanish
                  ? "Contact Email"
                  : "Correo Electrónico de Contacto"}
              </label>
              <InputWithCopy
                id={`e_mail_${declarationIndex + 1}`}
                value={declaration.e_mail || ""}
                placeholder={
                  !isSpanish
                    ? "e.g. email@example.com"
                    : "Ej. email@example.com"
                }
                onChange={(e) =>
                  handleChange(declarationIndex, "e_mail", e.target.value)
                }
                className="w-full border rounded px-2 py-1 pr-8"
                maxLength={70}
              />
            </div>
            <div className="form-group">
              <label htmlFor="contacto_telefono">
                {!isSpanish ? "Contact Phone" : "Teléfono de Contacto"}
              </label>
              <InputWithCopy
                id={`contacto_telefono_${declarationIndex + 1}`}
                value={declaration.contacto_telefono || ""}
                placeholder={!isSpanish ? "e.g. 916769999" : "Ej. 916769999"}
                onChange={(e) =>
                  handleChange(
                    declarationIndex,
                    "contacto_telefono",
                    e.target.value
                  )
                }
                className="w-full border rounded px-2 py-1 pr-8"
                maxLength={35}
              />
            </div>
          </div>
          <div className="form-group">
            <label
              htmlFor="naturaleza_transaccion"
            >
              {!isSpanish ? "Transaction Nature" : "Naturaleza Transacción"}
            </label>
            <InputWithCopy
              id={`naturaleza_transaccion_${declarationIndex + 1}`}
              value={declaration.naturaleza_transaccion}
              placeholder={`${
                !declaration.naturaleza_transaccion && !isSpanish
                  ? "Enter Transaction Nature"
                  : "Entrada Naturaleza Transacción"
              }`}
              onChange={(e) =>
                handleChange(
                  declarationIndex,
                  "naturaleza_transaccion",
                  e.target.value
                )
              }
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
          <div className="form-group">
            <label
              htmlFor="observaciones"
            >
              {!isSpanish ? "Observations" : "Observaciones"}
            </label>
            <InputWithCopy
              id={`observaciones_${declarationIndex + 1}`}
              value={declaration.observaciones}
              placeholder={`${
                !declaration.observaciones && !isSpanish
                  ? "Enter Observations"
                  : "Entrada Observaciones"
              }`}
              onChange={(e) =>
                handleChange(declarationIndex, "observaciones", e.target.value)
              }
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
        </div>
          </>
        )}

        {/* IMPORTES TAB */}
        {(activeTab === "All" || activeTab === "Importes") && (
          <>
        {/* Invoice Information */}
        <div className="split-modal flex flex-col gap-4 rounded-md flex-1 ">
          <h2 className="text-sm font-bold">
            {!isSpanish ? "Invoice Details" : "Detalles de Factura"}
          </h2>
          {/* New: Currency fields */}
          <div className="form-group">
            <label htmlFor="divisa">
              {!isSpanish
                ? "Currency Code (numeric)"
                : "Código Divisa (numérico)"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`divisa_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedDivisa ? "" : "opacity-75"
                }`}
                onClick={() => setDivisaModalOpen(true)}
              >
                {selectedDivisa ? (
                  <span className="truncate">
                    {selectedDivisa.codigo}{" "}
                    <span className="text-xs text-gray-400 ml-2">
                      ({selectedDivisa.descripcion})
                    </span>
                  </span>
                ) : declaration.divisa ? (
                  <span className="truncate" title={declaration.divisa}>
                    {declaration.divisa}
                  </span>
                ) : (
                  <span className="truncate">
                    {!isSpanish
                      ? "Select Currency Code"
                      : "Seleccionar Código Divisa"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              <DivisasModal
                divisas={divisas}
                isOpen={isDivisaModalOpen}
                onClose={() => setDivisaModalOpen(false)}
                onSelect={handleDivisaSelect}
                selectedDivisa={selectedDivisa}
                title={
                  !isSpanish
                    ? "Select Currency Code"
                    : "Seleccionar Código Divisa"
                }
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="cambio_divisa">
              {!isSpanish ? "Exchange Rate" : "Cambio Divisa"}
            </label>
            <InputWithCopy
              id={`cambio_divisa_${declarationIndex + 1}`}
              value={declaration.cambio_divisa || ""}
              placeholder={!isSpanish ? "e.g. 1,0" : "Ej. 1,0"}
              onChange={(e) =>
                handleChange(declarationIndex, "cambio_divisa", e.target.value)
              }
              className="w-full border rounded px-2 py-1 pr-8"
              maxLength={10}
            />
          </div>
          <div className="form-group">
            <label htmlFor="divisa_iso">
              {!isSpanish ? "Currency ISO (3-letter)" : "Divisa ISO (3 letras)"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`divisa_iso_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedDivisaIso ? "" : "opacity-75"
                }`}
                onClick={() => setDivisaIsoModalOpen(true)}
              >
                {selectedDivisaIso ? (
                  <span className="truncate">
                    {selectedDivisaIso.iso}{" "}
                    <span className="text-xs text-gray-400 ml-2">
                      ({selectedDivisaIso.descripcion})
                    </span>
                  </span>
                ) : declaration.divisa_iso ? (
                  <span className="truncate" title={declaration.divisa_iso}>
                    {declaration.divisa_iso}
                  </span>
                ) : (
                  <span className="truncate">
                    {!isSpanish
                      ? "Select Currency ISO"
                      : "Seleccionar Divisa ISO"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              <DivisasISOModal
                divisas={getDivisasISO()}
                isOpen={isDivisaIsoModalOpen}
                onClose={() => setDivisaIsoModalOpen(false)}
                onSelect={handleDivisaIsoSelect}
                selectedDivisaISO={selectedDivisaIso}
                title={
                  !isSpanish ? "Select Currency ISO" : "Seleccionar Divisa ISO"
                }
              />
            </div>
          </div>
          <div className="form-group">
            <label
              htmlFor="factura"
            >
              {!isSpanish ? "Invoice" : "Factura"}
            </label>
            <InputWithCopy
              id={`factura_${declarationIndex + 1}`}
              value={declaration.factura}
              placeholder={`${
                !declaration.factura && !isSpanish
                  ? "Enter Invoice"
                  : "Entrada Factura"
              }`}
              onChange={(e) =>
                handleChange(declarationIndex, "factura", e.target.value)
              }
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
          <div className="form-group">
            <label
              htmlFor="fecha_factura"
            >
              {!isSpanish ? "Invoice Date" : "Fecha Factura"}
            </label>
            <InputWithCopy
              id={`fecha_factura_${declarationIndex + 1}`}
              value={declaration.fecha_factura}
              placeholder={`${
                !declaration.fecha_factura && !isSpanish
                  ? "Enter Invoice Date"
                  : "Entrada Fecha Factura"
              }`}
              onChange={(e) =>
                handleChange(declarationIndex, "fecha_factura", e.target.value)
              }
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
          <div className="form-group">
            <label
              htmlFor="condiciones_entrega"
            >
              {!isSpanish ? "Delivery Terms" : "Condiciones Entrega"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  declaration.condiciones_entrega ? "" : "opacity-75"
                }`}
                onClick={() => setCondicionesEntregaModalOpen(true)}
                id={`condiciones_entrega_${declarationIndex + 1}`}
              >
                {(() => {
                  const condiciones = getCondicionesEntrega();
                  const selectedCondicion = condiciones.find(
                    (c) => c.codigo === declaration.condiciones_entrega
                  );
                  if (selectedCondicion) {
                    return (
                      <span className="truncate">
                        {selectedCondicion.codigo}{" "}
                        <span className="text-xs text-gray-400 ml-2">
                          ({selectedCondicion.nombre})
                        </span>
                      </span>
                    );
                  } else if (declaration.condiciones_entrega) {
                    return (
                      <span
                        className="truncate"
                        title={declaration.condiciones_entrega}
                      >
                        {declaration.condiciones_entrega}
                      </span>
                    );
                  } else {
                    return (
                      <span className="truncate">
                        {!isSpanish
                          ? "Select Delivery Terms"
                          : "Seleccionar Condición de Entrega"}
                      </span>
                    );
                  }
                })()}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              <CondicionesEntregaModal
                condiciones={getCondicionesEntrega()}
                isOpen={isCondicionesEntregaModalOpen}
                onClose={() => setCondicionesEntregaModalOpen(false)}
                onSelect={(cond) =>
                  handleChange(
                    declarationIndex,
                    "condiciones_entrega",
                    cond.codigo
                  )
                }
                selectedCondicion={getCondicionesEntrega().find(
                  (c) => c.codigo === declaration.condiciones_entrega
                )}
                title={
                  !isSpanish
                    ? "Select Delivery Terms"
                    : "Seleccionar Condición de Entrega"
                }
              />
            </div>
          </div>
        </div>
        {/* Countries */}
        <div className="split-modal flex flex-col gap-4 rounded-md flex-1">
          <h2 className="text-sm font-bold">
            {!isSpanish ? "Country Information" : "Información de Países"}
          </h2>
          {/* New: Location fields */}
          <div className="form-group">
            <label htmlFor="pais_ubicacion">
              {!isSpanish
                ? "Location Country (2-letter ISO)"
                : "País Ubicación (ISO 2 letras)"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`pais_ubicacion_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedPaisUbicacion ? "" : "opacity-75"
                }`}
                onClick={() => setPaisUbicacionModalOpen(true)}
              >
                {selectedPaisUbicacion ? (
                  <span className="truncate">
                    {selectedPaisUbicacion.codigo}{" "}
                    {selectedPaisUbicacion.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedPaisUbicacion.nombre})
                      </span>
                    )}
                  </span>
                ) : declaration.pais_ubicacion ? (
                  <span className="truncate" title={declaration.pais_ubicacion}>
                    {declaration.pais_ubicacion}
                  </span>
                ) : (
                  <span className="truncate">
                    {!isSpanish
                      ? "Select Location Country"
                      : "Seleccionar País Ubicación"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              <PaisModal
                countries={paises}
                isOpen={isPaisUbicacionModalOpen}
                onClose={() => setPaisUbicacionModalOpen(false)}
                onSelect={handlePaisUbicacionSelect}
                selectedCountry={selectedPaisUbicacion}
                title={
                  !isSpanish
                    ? "Select Location Country"
                    : "Seleccionar País Ubicación"
                }
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="tipo_ubicacion">
              {!isSpanish
                ? "Location Type (A/B/C/D)"
                : "Tipo Ubicación (A/B/C/D)"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`tipo_ubicacion_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedTipoUbicacion ? "" : "opacity-75"
                }`}
                onClick={() => setTipoUbicacionModalOpen(true)}
              >
                {selectedTipoUbicacion ? (
                  <span className="truncate">
                    {selectedTipoUbicacion.codigo}{" "}
                    {selectedTipoUbicacion.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedTipoUbicacion.nombre})
                      </span>
                    )}
                  </span>
                ) : declaration.tipo_ubicacion ? (
                  <span className="truncate" title={declaration.tipo_ubicacion}>
                    {declaration.tipo_ubicacion}
                  </span>
                ) : (
                  <span className="truncate">
                    {!isSpanish
                      ? "Select Location Type"
                      : "Seleccionar Tipo de Ubicación"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              <TipoUbicacionModal
                tipos={tipoUbicacionOptions}
                isOpen={isTipoUbicacionModalOpen}
                onClose={() => setTipoUbicacionModalOpen(false)}
                onSelect={handleTipoUbicacionSelect}
                selectedTipo={selectedTipoUbicacion}
                title={
                  !isSpanish
                    ? "Select Location Type"
                    : "Seleccionar Tipo de Ubicación"
                }
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="localizacion">
              {!isSpanish ? "Location Code" : "Código de Localización"}
            </label>
            <InputWithCopy
              id={`localizacion_${declarationIndex + 1}`}
              value={declaration.localizacion || ""}
              placeholder={!isSpanish ? "e.g. 280112345" : "Ej. 280112345"}
              onChange={(e) =>
                handleChange(declarationIndex, "localizacion", e.target.value)
              }
              className="w-full border rounded px-2 py-1 pr-8"
              maxLength={35}
            />
          </div>
          {/* Lugar Entrega - Missing fields from PDF */}
          <div className="form-group">
            <label htmlFor="codigo_un">
              {!isSpanish ? "UN Code (Delivery Place)" : "Código UN (Lugar Entrega)"}
            </label>
            <InputWithCopy
              id={`codigo_un_${declarationIndex + 1}`}
              value={declaration.codigo_un || ""}
              placeholder={!isSpanish ? "e.g. CASUY" : "Ej. CASUY"}
              onChange={(e) =>
                handleChange(declarationIndex, "codigo_un", e.target.value)
              }
              className="w-full border rounded px-2 py-1 pr-8"
              maxLength={35}
            />
          </div>
          <div className="form-group">
            <label htmlFor="lugar_entrega">
              {!isSpanish ? "Delivery Place" : "Lugar Entrega"}
            </label>
            <InputWithCopy
              id={`lugar_entrega_${declarationIndex + 1}`}
              value={declaration.lugar_entrega || ""}
              placeholder={!isSpanish ? "e.g. Surrey" : "Ej. Surrey"}
              onChange={(e) =>
                handleChange(declarationIndex, "lugar_entrega", e.target.value)
              }
              className="w-full border rounded px-2 py-1 pr-8"
              maxLength={70}
            />
          </div>
          {/* Tipo Autorización C.D. and Ident. Adicional - Missing fields from PDF */}
          <div className="form-group">
            <label htmlFor="tipo_autorizacion_cd">
              {!isSpanish
                ? "Type (49 Autorización C.D.)"
                : "Tipo (49 Autorización C.D.)"}
            </label>
            <InputWithCopy
              id={`tipo_autorizacion_cd_${declarationIndex + 1}`}
              value={declaration.tipo_autorizacion_cd || ""}
              placeholder={`${
                !declaration.tipo_autorizacion_cd && !isSpanish
                  ? "Enter Type"
                  : "Entrada Tipo"
              }`}
              onChange={(e) =>
                handleChange(declarationIndex, "tipo_autorizacion_cd", e.target.value)
              }
              className="w-full border rounded px-2 py-1 pr-8"
              maxLength={10}
            />
          </div>
          <div className="form-group">
            <label htmlFor="identificacion_adicional">
              {!isSpanish
                ? "Additional Identification"
                : "Identificación Adicional"}
            </label>
            <InputWithCopy
              id={`identificacion_adicional_${declarationIndex + 1}`}
              value={declaration.identificacion_adicional || ""}
              placeholder={`${
                !declaration.identificacion_adicional && !isSpanish
                  ? "Enter Additional Identification"
                  : "Entrada Identificación Adicional"
              }`}
              onChange={(e) =>
                handleChange(declarationIndex, "identificacion_adicional", e.target.value)
              }
              className="w-full border rounded px-2 py-1 pr-8"
              maxLength={35}
            />
          </div>
          <div className="form-group">
            <label
              htmlFor="pais_destino"
            >
              {!isSpanish ? "Destination Country" : "País Destino"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`pais_destino_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedPaisDestino ? "" : "opacity-75"
                }`}
                onClick={() => setPaisDestinoModalOpen(true)}
              >
                {selectedPaisDestino ? (
                  <span className="truncate">
                    {selectedPaisDestino.codigo}{" "}
                    {selectedPaisDestino.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedPaisDestino.nombre})
                      </span>
                    )}
                  </span>
                ) : declaration.pais_destino ? (
                  <span className="truncate" title={declaration.pais_destino}>
                    {declaration.pais_destino}
                  </span>
                ) : (
                  <span className="truncate">
                    {!isSpanish
                      ? "Select Destination Country"
                      : "Seleccionar País Destino"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              <PaisModal
                countries={paises}
                isOpen={isPaisDestinoModalOpen}
                onClose={() => setPaisDestinoModalOpen(false)}
                onSelect={handlePaisDestinoSelect}
                selectedCountry={selectedPaisDestino}
                title={
                  !isSpanish
                    ? "Select Destination Country"
                    : "Seleccionar País Destino"
                }
              />
            </div>
          </div>
          <div className="form-group">
            <label
              htmlFor="pais_exportacion"
            >
              {!isSpanish ? "Export Country" : "País Exportación"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`pais_exportacion_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedPaisExportacion ? "" : "opacity-75"
                }`}
                onClick={() => setPaisExportacionModalOpen(true)}
              >
                {selectedPaisExportacion ? (
                  <span className="truncate">
                    {selectedPaisExportacion.codigo}{" "}
                    {selectedPaisExportacion.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedPaisExportacion.nombre})
                      </span>
                    )}
                  </span>
                ) : declaration.pais_exportacion ? (
                  <span
                    className="truncate"
                    title={declaration.pais_exportacion}
                  >
                    {declaration.pais_exportacion}
                  </span>
                ) : (
                  <span className="truncate">
                    {!isSpanish
                      ? "Select Export Country"
                      : "Seleccionar País Exportación"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              <PaisModal
                countries={paises}
                isOpen={isPaisExportacionModalOpen}
                onClose={() => setPaisExportacionModalOpen(false)}
                onSelect={handlePaisExportacionSelect}
                selectedCountry={selectedPaisExportacion}
                title={
                  !isSpanish
                    ? "Select Export Country"
                    : "Seleccionar País Exportación"
                }
              />
            </div>
          </div>
          <div className="form-group">
            <label
              htmlFor="pais_transporte_frontera"
            >
              {!isSpanish
                ? "Border Transport Country"
                : "País Transporte Frontera"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`pais_transporte_frontera_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedPaisTransporteFrontera ? "" : "opacity-75"
                }`}
                onClick={() => setPaisTransporteFronteraModalOpen(true)}
              >
                {selectedPaisTransporteFrontera ? (
                  <span className="truncate">
                    {selectedPaisTransporteFrontera.codigo}{" "}
                    {selectedPaisTransporteFrontera.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedPaisTransporteFrontera.nombre})
                      </span>
                    )}
                  </span>
                ) : declaration.pais_transporte_frontera ? (
                  <span
                    className="truncate"
                    title={declaration.pais_transporte_frontera}
                  >
                    {declaration.pais_transporte_frontera}
                  </span>
                ) : (
                  <span className="truncate">
                    {!isSpanish
                      ? "Select Border Transport Country"
                      : "Seleccionar País Transporte Frontera"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              <PaisModal
                countries={paises}
                isOpen={isPaisTransporteFronteraModalOpen}
                onClose={() => setPaisTransporteFronteraModalOpen(false)}
                onSelect={handlePaisTransporteFronteraSelect}
                selectedCountry={selectedPaisTransporteFrontera}
                title={
                  !isSpanish
                    ? "Select Border Transport Country"
                    : "Seleccionar País Transporte Frontera"
                }
              />
            </div>
          </div>
        </div>
          </>
        )}

      </div>
    </>
  );
};
